import { useQuery } from '@apollo/client';
import {
  GlobeAltIcon,
  MapPinIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { LoadingOrError, mapHref, RcLink } from '@retainerclub/common';
import { GET_PARTNER_LOC } from '@retainerclub/customer-api';
import { useAccount } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { formatPhoneNumber } from 'react-phone-number-input';

function DashboardLayoutTitleWithPartner({ heading, title, subtitle = null }) {
  const [location, setLocation] = useState(null);
  const { partnerLocationId } = useAccount();

  const { error, loading } = useQuery(GET_PARTNER_LOC, {
    variables: {
      id: partnerLocationId,
    },
    skip: partnerLocationId === null || partnerLocationId === undefined,
    onCompleted: (data) => {
      setLocation(data.partnerLocation);
    },
  });

  const shouldRenderPartner = LoadingOrError({
    loading,
    error,
    data: { partnerLocation: location },
    dataKey: 'partnerLocation',
    title: 'Partner Location',
  });

  return (
    <div className="z-10 mx-auto w-full max-w-full px-4 md:max-w-7xl md:px-8 lg:px-4">
      <div className="relative py-8">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
          {/* Main Title Section */}
          <div className="flex-1">
            <h2 className="font-bold font-oswald text-rcprimary-500 text-xl uppercase tracking-wider">
              {heading}
            </h2>
            <div className="my-2 w-2/3 border-rcprimary-500 border-b-3 py-1" />
            <h1 className="mt-4 font-bold text-4xl text-black leading-none tracking-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-2 font-medium text-black/70 text-xl leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>

          {partnerLocationId && shouldRenderPartner === true && location && (
            <div className="shrink-0 lg:max-w-sm">
              <div className="flex flex-row gap-6">
                {location.imageUrl && (
                  <div className="size-28 shrink-0 rounded-lg bg-white">
                    <img
                      className="size-full rounded-lg object-cover"
                      src={location.imageUrl}
                      alt={location.name}
                    />
                  </div>
                )}
                <div className="flex flex-col items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-rcprimary-400 text-sm">
                      Your Provider
                    </p>
                    <h3 className="font-bold text-base text-rcgray-900 leading-tight">
                      {location.name}
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {location.address && (
                      <div className="flex items-start gap-2">
                        <MapPinIcon
                          className="mt-0.5 size-4 shrink-0 text-gray-400"
                          aria-hidden="true"
                        />
                        <div className="text-gray-600 text-xs leading-relaxed">
                          <RcLink
                            href={mapHref(null, null, location.address)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <div>{location.address.address}</div>
                            {location.address.address2 && (
                              <div>{location.address.address2}</div>
                            )}
                            <div>
                              {location.address.city}, {location.address.region}{' '}
                              {location.address.postalCode}
                            </div>
                          </RcLink>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-4">
                      {location.phone && (
                        <div className="flex items-center gap-1">
                          <PhoneIcon
                            className="size-4 shrink-0 text-gray-400"
                            aria-hidden="true"
                          />
                          <RcLink
                            href={`tel:${location.phone}`}
                            className="text-rcprimary-500 text-xs hover:text-rcprimary-600"
                          >
                            {formatPhoneNumber(location.phone)}
                          </RcLink>
                        </div>
                      )}

                      {location.website && (
                        <div className="flex items-center gap-1">
                          <GlobeAltIcon
                            className="size-4 shrink-0 text-gray-400"
                            aria-hidden="true"
                          />
                          <RcLink
                            href={`//${location.website}`}
                            className="text-rcprimary-500 text-xs hover:text-rcprimary-600"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Website
                          </RcLink>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

DashboardLayoutTitleWithPartner.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  heading: PropTypes.string.isRequired,
};

export default DashboardLayoutTitleWithPartner;
